import {
  AddFolderAttachmentsResponse,
  DeleteInquiryResponse,
  EditInquiryResponse,
  GetDocumentIdsResponse,
  GetDocumentResponse,
  GetInquiriesResponse,
  GetInquiryByIdResponse,
  GetInquiryFilesStatusResponse,
  GetInquiryHistoryResponse,
  PostChatResponse,
  RemoveFileResponse,
  RemoveFolderAttachmentsResponse,
  ReplaceSelectionPayload,
  ReplaceSelectionResponse,
} from '@/types/chat/chat';
import { httpServices } from '../axiosClient';
import { AddFilesResponse, RemoveResponse } from '@/types/folders/folder';
import { getDisplayLanguage, type Locale } from '@/utils/locale';
import { useTranslation } from 'react-i18next';

export const getInquiry = async (data: any) => {
  return await httpServices.llmService.get<GetInquiriesResponse>(
    '/chat/inquiry',
    data,
  );
};

export const getInquiryById = async (inquiry_id: string) => {
  const response = await httpServices.llmService.get<GetInquiryByIdResponse>(
    `/chat/inquiry/${inquiry_id}`,
  );
  return response;
};

/**
 * Get the processing status of every file attached to an inquiry.
 * GET /llm/chat/inquiry/files-status/{INQUIRY_ID}
 */
export const getInquiryFilesStatus = async (inquiry_id: string) => {
  return await httpServices.llmService.get<GetInquiryFilesStatusResponse>(
    `/chat/inquiry/files-status/${inquiry_id}`,
  );
};

/**
 * Replace the whole file/folder selection of an inquiry.
 * POST /llm/chat/inquiry/{INQUIRY_ID}/selection
 *
 * `base_version` is the selection version the payload was derived from; the
 * backend applies the write only while that version is still current.
 */
export const replaceInquirySelection = async (
  inquiry_id: string,
  payload: ReplaceSelectionPayload,
) => {
  return await httpServices.llmService.post<ReplaceSelectionResponse>(
    `/chat/inquiry/${inquiry_id}/selection`,
    payload,
  );
};

/**
 * Remove a single file from an inquiry.
 * DELETE /llm/chat/inquiry/{INQUIRY_ID}/files/{FILE_ID}
 */
export const removeInquiryFile = async (
  inquiry_id: string,
  file_id: string,
) => {
  return await httpServices.llmService.delete<RemoveFileResponse>(
    `/chat/inquiry/${inquiry_id}/files/${file_id}`,
  );
};

export const createInquiry = async (data: any, params: any) => {
  const response = await httpServices.llmService.post(
    '/chat/inquiry',
    data,
    params,
  );
  return response;
};

export const editInquiry = async (inquiry_id: string, data: any) => {
  const response = await httpServices.llmService.post<EditInquiryResponse>(
    `/chat/inquiry/${inquiry_id}/rename`,
    {},
    data,
  );
  return response;
};

export const deleteInquiry = async (data: any) => {
  const response = await httpServices.llmService.delete<DeleteInquiryResponse>(
    `/chat/inquiry/${data.inquiry_id}`,
  );
  return response;
};

export const getAllDocumentIdsFromFolder = async (inquiry_id: string) => {
  const response = await httpServices.llmService.get<GetDocumentIdsResponse>(
    `/chat/inquiry/${inquiry_id}/documents`,
  );
  return response;
};

export const getInquiryHistory = async (inquiryId: string, params: any) => {
  const response = await httpServices.llmService.get<GetInquiryHistoryResponse>(
    `/chat/message/${inquiryId}`,
    params,
  );
  return response;
};

export const addAttachments = async (inquiry_id: string, data: any) => {
  const response = await httpServices.llmService.post<AddFilesResponse>(
    `/chat/document/${inquiry_id}/add_attachments`,
    data,
    {},
    { 'Content-Type': 'multipart/form-data' },
  );
  return response;
};

export const removeAttachments = async (inquiry_id: string, data: any) => {
  return await httpServices.llmService.post<RemoveResponse>(
    `/chat/document/${inquiry_id}/remove_attachments`,
    data,
  );
};

export const addInquiryFolder = async (inquiry_id: string, data: any) => {
  return await httpServices.llmService.post<AddFolderAttachmentsResponse>(
    `/chat/document/${inquiry_id}/add_folders`,
    data,
  );
};

export const removeInquiryFolder = async (inquiry_id: string, data: any) => {
  return await httpServices.llmService.post<RemoveFolderAttachmentsResponse>(
    `/chat/document/${inquiry_id}/remove_folders`,
    data,
  );
};

export const postChat = async (data: any, params: any) => {
  return await httpServices.llmService.post<PostChatResponse>(
    '/chat/message',
    data,
    params,
  );
};

export const updateSystemPrompt = async (inquiry_id: any, data: any) => {
  return await httpServices.llmService.post<GetInquiryByIdResponse>(
    `/chat/inquiry/${inquiry_id}/prompt`,
    data,
  );
};

export const getDocumentLinks = async (inquiryId: string, missingIds: any) => {
  const response = await httpServices.llmService.post<GetDocumentResponse>(
    `/chat/inquiry/${inquiryId}/attachments`,
    missingIds,
    {},
    { 'Content-Type': 'multipart/form-data' },
  );
  return response;
};

export const exportInquiryCSV = async (
  inquiryId: string,
  displayLanguage: string,
) => {
  try {
    const response = await httpServices.llmService.axiosInstance.get(
      `/chat/inquiry/${inquiryId}/export?lang=${displayLanguage}`,
      {
        responseType: 'blob',
      },
    );

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inquiry_${inquiryId}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error: any) {
    let errorMessage = error.message;
    if (error.response?.data) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        errorMessage = json.detail || json.error || errorMessage;
      } catch {
        errorMessage = error.response?.data?.detail || errorMessage;
      }
    }
    return {
      success: false,
      error: errorMessage,
    };
  }
};
